import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, Loader2, UserMinus, X } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { externalHandoverPreview, externalRevokeResult } from '../lib/types'

/**
 * Revoke an external collaborator WITH handover. Unlike the old one-click
 * revoke, this first moves the collaborator's in-flight work (tasks they're
 * assigned to + tasks they created, plus approvals/goals) to an internal
 * colleague, then cuts access. A destination is only REQUIRED when they
 * actually hold work — a work-free collaborator revokes with just the
 * confirmation. Mirrors the internal ExitHandoverDialog.
 */
export function ExternalRevokeDialog({ open, seatId, collaboratorName, onClose, onRevoked }) {
  const qc = useQueryClient()
  const [destinationUserId, setDestinationUserId] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  // Re-arm the picker + checkbox whenever the dialog closes or targets a
  // different collaborator, so a destination picked for one is never carried
  // onto another.
  useEffect(() => {
    if (!open) {
      setDestinationUserId('')
      setConfirmed(false)
    }
  }, [open, seatId])

  // Escape closes; backdrop click does not (matches the shared dismissal model).
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const previewQuery = useQuery({
    queryKey: ['external-handover', seatId],
    queryFn: async () => {
      const res = await api.get(`/external-collaborators/${seatId}/handover/preview`)
      return externalHandoverPreview.parse(res.data)
    },
    enabled: open && Boolean(seatId),
  })

  const revokeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/external-collaborators/${seatId}/revoke`, {
        destinationUserId: destinationUserId || undefined,
      })
      return externalRevokeResult.parse(res.data)
    },
    onSuccess: (result) => {
      const moved = result.reassigned
        ? result.reassigned.reassignedAssignments + result.reassigned.reassignedCreated
        : 0
      toast.success(
        moved > 0
          ? `Handed over ${moved} item${moved === 1 ? '' : 's'} and revoked access.`
          : 'External collaborator access revoked.',
      )
      void qc.invalidateQueries({ queryKey: ['external-collaborators'] })
      onRevoked?.()
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (!open || !seatId) return null

  const hasWorkload = previewQuery.data?.hasWorkload === true
  const summary = previewQuery.data?.summary
  const candidates = (previewQuery.data?.candidates ?? []).filter(
    (m) => m.status === 'active',
  )
  const fallbackToAll = previewQuery.data?.fallbackToAll === true
  // A destination is required only when there is work to move.
  const destinationReady = !hasWorkload || Boolean(destinationUserId)
  const canCommit = confirmed && destinationReady && !previewQuery.isLoading && !previewQuery.isError

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="w-[640px] max-h-[90vh] flex flex-col rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <p className="font-semibold text-[color:var(--color-ink-strong)] inline-flex items-center gap-2">
            <UserMinus size={16} />
            Revoke access{collaboratorName ? ` — ${collaboratorName}` : ''}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div className="rounded-md border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-3 text-xs text-[color:var(--color-ink)] inline-flex items-start gap-2">
            <AlertTriangle size={14} className="text-[color:var(--color-warning)] shrink-0 mt-0.5" />
            <p>
              <strong>This cuts the collaborator's access.</strong> Any work
              they hold is first handed over to the colleague you pick below —
              tasks they're assigned to and tasks they created move across, their
              pending approvals are cleared, and goals they own are reassigned.
              Access is revoked only after the handover succeeds. You would need
              to invite them again to restore access.
            </p>
          </div>

          {previewQuery.isLoading ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" />
              Checking their workload…
            </p>
          ) : previewQuery.isError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              {extractApiError(previewQuery.error).message}
            </p>
          ) : summary ? (
            <div className="grid grid-cols-2 gap-3">
              <PreviewStat label="Tasks assigned" value={summary.tasksAssignedCount} />
              <PreviewStat label="Tasks created" value={summary.tasksCreatedCount} />
              <PreviewStat label="Pending approvals" value={summary.pendingApprovalsCount} />
              <PreviewStat label="Goals owned" value={summary.goalsCount} />
            </div>
          ) : null}

          {/* Destination picker — shown only when there is work to hand over. */}
          {hasWorkload ? (
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
                Hand their work over to
                <span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
              </label>
              <select
                value={destinationUserId}
                onChange={(e) => setDestinationUserId(e.target.value)}
                disabled={previewQuery.isLoading || previewQuery.isError}
                className="w-full h-10 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="" disabled>
                  {candidates.length === 0 ? 'No active colleagues available' : 'Pick a colleague'}
                </option>
                {candidates.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName} — {m.emailId}
                    {m.sharedDepartments && m.sharedDepartments.length > 0
                      ? ` · ${m.sharedDepartments.join(', ')}`
                      : ''}
                  </option>
                ))}
              </select>
              {!previewQuery.isLoading && !previewQuery.isError && candidates.length > 0 ? (
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  {fallbackToAll
                    ? 'Showing every active internal member (the collaborator has no granted department).'
                    : 'Showing active members of the department(s) this collaborator was granted.'}
                </p>
              ) : null}
              {!previewQuery.isLoading && !previewQuery.isError && candidates.length === 0 ? (
                <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  No active internal colleague is available to receive this work. Add one before revoking.
                </p>
              ) : null}
            </div>
          ) : !previewQuery.isLoading && !previewQuery.isError ? (
            <p className="text-sm text-[color:var(--color-ink-muted)]">
              This collaborator holds no open work — nothing to hand over.
            </p>
          ) : null}

          <label className="inline-flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-[color:var(--color-ink)]">
              I understand this will revoke{' '}
              <span className="font-semibold">{collaboratorName ?? 'this collaborator'}</span>
              's access{hasWorkload ? ' and hand their work to the colleague above' : ''}. This cannot be undone in one click.
            </span>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-sm border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => revokeMutation.mutate()}
            disabled={revokeMutation.isPending || !canCommit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-[color:var(--color-danger)] text-white hover:opacity-90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={12} />
            {revokeMutation.isPending
              ? 'Revoking…'
              : hasWorkload
                ? 'Hand over & revoke'
                : 'Revoke access'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PreviewStat({ label, value }) {
  return (
    <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
      <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums">
        {value.toLocaleString('en-GB')}
      </p>
    </div>
  )
}
